import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTooltip } from '@angular/material/tooltip';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  DeviceHeatingCircuitIdCommands,
  DeviceIds,
  DeviceRoles,
  Feature,
  FeatureNames,
  FeaturePropertyKey,
  IsNumberValueProperty,
  RoomCommands,
  RoomProperties,
} from '@vi-iot/client';

import { GTMService } from '@services';
import {
  GatewayDevice,
  GatewayStore,
  getFhtMainFromChannel,
  InstallationFeaturesStore,
} from '@store';

import { matTooltipSetter } from '../../../../../shared/functions/mat-tooltip-setter/mat-tooltip-setter';
import { CommissioningService } from '../../commissioning/commissioning.service';

@Component({
  selector: 'app-manage-room-modal',
  templateUrl: './manage-room-modal.component.html',
  styleUrls: ['./manage-room-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManageRoomModalComponent implements OnInit, OnDestroy {
  readonly featurePropertyKey = FeaturePropertyKey;
  @ViewChildren(MatTooltip)
  set matTooltip(queryList: QueryList<MatTooltip>) {
    matTooltipSetter(queryList);
  }

  private ngUnsubscribe$: Subject<void> = new Subject<void>();
  room$: Observable<Feature<RoomProperties, RoomCommands>>;

  constructor(
    public dialogRef: MatDialogRef<ManageRoomModalComponent>,
    @Inject(MAT_DIALOG_DATA)
    public roomFeature: string,
    public gatewayStore: GatewayStore,
    public installationFeaturesStore: InstallationFeaturesStore,
    public commissioningService: CommissioningService,
    private gtmService: GTMService
  ) {}

  ngOnInit(): void {
    this.room$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      this.roomFeature,
      DeviceIds.RoomControl
    );
  }

  openEditRoomDialog(room: Feature<RoomProperties, RoomCommands>): void {
    this.commissioningService.openEditRoomDialog(room);
  }

  onUnassignDeviceFromRoom(deviceId: string, room: Feature<RoomProperties, RoomCommands>): void {
    this.commissioningService.unassignDeviceFromRoom(deviceId, room);
    this.dialogRef.close();
    this.gtmService.track('onConfirm', 'Unassign device from room', 'manageRoom');
  }

  cancelUnassignDeviceFromRoomConfirmation(): void {
    this.gtmService.track('onCancel', 'Unassign device from room confirmation', 'manageRoom');
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onAdd(device: GatewayDevice): void {
    let deviceId = device.id;

    if (device.roles.includes(DeviceRoles.FhtChannel)) {
      deviceId = getFhtMainFromChannel(device.id);
    }

    this.installationFeaturesStore
      .selectInstallationDeviceFeature<IsNumberValueProperty, DeviceHeatingCircuitIdCommands>(
        FeatureNames.deviceHeatingCircuitId,
        deviceId
      )
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((deviceHeatingCircuit: Feature<IsNumberValueProperty, never>) => {
        const hcId = deviceHeatingCircuit?.properties?.value?.value;

        this.dialogRef.close({
          deviceId: device.id,
          heatingCircuitId: hcId,
        });
      });
  }

  onAddNewDevice(room: Feature<RoomProperties, RoomCommands>): void {
    this.commissioningService.openAddDeviceDialog(room.feature);
    this.onCancel();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
